/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation, useParams } from "react-router-dom";

import {
  Box,
  CssBaseline,
  Paper as MuiPaper,
  Container as MuiContainer,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";
import { GlobalStyle } from "../components/mira/GlobalStyle";
import {
  createCirromSidebarItems,
  createDataProjectSidebarItems,
} from "../components/mira/components/sidebar/cirromSidebarItems";
import { CirromSidebar } from "../components/mira/components/sidebar/CirromSidebar";
import { MiraTheme } from "../components/mira/theme/theme";
import Navbar from "../components/mira/components/navbar/Navbar";
import { Footer } from "../components/mira/components/Footer";
import { useAuth } from "../hooks/useAuth";
import { ThemeProvider } from "../components/mira/theme/ThemeContext";
import { useTheme as useMiraTheme } from "../components/mira/theme/useTheme";
import { IntegratedSystem } from "../api/integratedsystems";
import { DataProject } from "../api/dataprojects";
import { useSyncIntegratedSystems } from "../hooks/useSyncIntegratedSystems";
import { SideBarWidthContext } from "../App";
import {
  getReferenceDataSyncSystems,
  StandaloneSyncSystem,
} from "../api/datasystems";

const drawerWidth = 258;
const drawerWidthCollapsed = 64;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div<{ theme?: MiraTheme; isCollapsed?: boolean }>`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) =>
      props.isCollapsed ? drawerWidthCollapsed : drawerWidth}px;
    flex-shrink: 0;
    transition: width 225ms cubic-bezier(0.4, 0, 0.6, 1);
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 0;
`;

const Paper = styled(MuiPaper)(spacing);

const Container = styled(MuiContainer)`
  height: 100%;
`;

const MainContent = styled(Paper)<{ theme?: MiraTheme }>`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface CirromContentType {
  integratedSystemsForUser?: IntegratedSystem[];
  selectedSystemId: number;
  setSelectedSystemId?: (id: number) => void;
  dataProjects?: DataProject[];
  isDataProject: boolean;
  children?: React.ReactNode;
}

export const CirromContent: React.FC<CirromContentType> = ({
  integratedSystemsForUser,
  selectedSystemId,
  setSelectedSystemId,
  dataProjects,
  isDataProject,
  children,
}) => {
  const router = useLocation();
  const params = useParams<{
    integratedSystemId?: string;
    dataProjectId?: string;
  }>();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [enrichSystems, setEnrichSystems] = useState<StandaloneSyncSystem[]>(
    []
  );
  const { syncIntegratedSystems } = useSyncIntegratedSystems();
  const auth = useAuth();
  const sideBarWidth = useContext(SideBarWidthContext);

  const currentUser = auth.currentUser;
  const isCollapsed = sideBarWidth.value === "small";

  // Use the URL parameter if available to ensure sidebar stays in sync with the URL
  // This applies to both integrated systems and data projects
  const effectiveSystemId = React.useMemo(() => {
    const candidateIds = isDataProject
      ? [params.dataProjectId, params.integratedSystemId]
      : [params.integratedSystemId];

    for (const candidateId of candidateIds) {
      if (!candidateId) {
        continue;
      }

      const parsed = Number.parseInt(candidateId, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }

      console.warn(`Invalid route id in URL: ${candidateId}`);
    }

    return selectedSystemId;
  }, [
    isDataProject,
    params.dataProjectId,
    params.integratedSystemId,
    selectedSystemId,
  ]);

  // Resolve the actual integrated system ID for contact search operations
  // When on data-project routes, effectiveSystemId is the data project ID,
  // so we need to look up the integrated_system_id from the data project
  const integratedSystemIdForSearch = React.useMemo(() => {
    if (isDataProject && dataProjects) {
      const dataProject = dataProjects.find(
        (dp) => dp.id === effectiveSystemId
      );
      if (dataProject) {
        return dataProject.integrated_system_id;
      }
    }
    return effectiveSystemId;
  }, [isDataProject, dataProjects, effectiveSystemId]);

  // Find current data project when in data project mode (for sidebar pill)
  const currentDataProject = React.useMemo(() => {
    if (isDataProject && dataProjects) {
      return dataProjects.find((dp) => dp.id === effectiveSystemId);
    }
    return undefined;
  }, [isDataProject, dataProjects, effectiveSystemId]);

  const sidebarSyncGroups = useMemo(
    () =>
      !integratedSystemIdForSearch || isDataProject
        ? []
        : syncIntegratedSystems.filter(
            (s) =>
              s.system_a_id === integratedSystemIdForSearch ||
              s.system_b_id === integratedSystemIdForSearch
          ),
    [syncIntegratedSystems, integratedSystemIdForSearch, isDataProject]
  );

  useEffect(() => {
    let ignore = false;

    getReferenceDataSyncSystems()
      .then((systems) => {
        if (!ignore) {
          setEnrichSystems(systems);
        }
      })
      .catch((err) => {
        if (!ignore) {
          console.error(err);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  // Determine which sidebar items to use based on context
  const sidebarItems = isDataProject
    ? createDataProjectSidebarItems(
        integratedSystemsForUser || [],
        effectiveSystemId,
        integratedSystemIdForSearch,
        enrichSystems
      )
    : createCirromSidebarItems(
        integratedSystemsForUser || [],
        integratedSystemIdForSearch,
        sidebarSyncGroups,
        enrichSystems
      );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const theme = useTheme();
  const miraTheme = useMiraTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    //@ts-ignore
    <ThemeProvider theme={miraTheme.theme}>
      <Root>
        <CssBaseline />
        <GlobalStyle />
        <Drawer isCollapsed={isCollapsed}>
          {/* Mobile drawer */}
          <Box sx={{ display: { xs: "block", lg: "none" } }}>
            <CirromSidebar
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              items={sidebarItems}
              currentUser={currentUser}
              integratedSystems={integratedSystemsForUser}
              selectedSystemId={integratedSystemIdForSearch}
              setSelectedSystemId={setSelectedSystemId}
              isDataProject={isDataProject}
              currentDataProject={currentDataProject}
            />
          </Box>
          {/* Desktop drawer - CirromSidebar now handles both collapsed and expanded states */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <CirromSidebar
              PaperProps={{ style: { width: drawerWidth } }}
              items={sidebarItems}
              currentUser={currentUser}
              integratedSystems={integratedSystemsForUser}
              selectedSystemId={integratedSystemIdForSearch}
              setSelectedSystemId={setSelectedSystemId}
              isDataProject={isDataProject}
              currentDataProject={currentDataProject}
            />
          </Box>
        </Drawer>
        <AppContent>
          <Navbar
            onDrawerToggle={handleDrawerToggle}
            dataProjects={dataProjects}
            selectedSystemId={integratedSystemIdForSearch}
          />
          <MainContent p={5}>
            <Container style={{ width: "100%", maxWidth: "inherit" }}>
              {children}
              <Outlet />
            </Container>
          </MainContent>
          <Footer />
        </AppContent>
        {/*<Settings />*/}
      </Root>
    </ThemeProvider>
  );
};
